// A saved connection's sync health, read from what GET /api/connector/connections/{id}
// returns: `last_run` (the connection's newest sync_run_history row), `schedule` (its
// sync_schedules row) and `history_error` / `schedule_error` when either could not be read
// (services/connection_status.py). `test_status` / `last_tested` are NOT used for health:
// they are written when the credentials are saved and no sync ever updates them.
//
// Plain module, no imports, so tests can run it under node.

const STATUS_WORD = {
  completed: 'completed',
  success: 'completed',
  completed_with_errors: 'completed with errors',
  failed: 'failed',
  interrupted: 'interrupted',
  running: 'running',
}

// tone: healthy | partial | error | running | never | unknown
const TONE = {
  completed: 'healthy',
  success: 'healthy',
  completed_with_errors: 'partial',
  failed: 'error',
  interrupted: 'error',
  running: 'running',
}

function toDate(ts) {
  if (!ts) return null
  const d = new Date(ts)
  return Number.isNaN(d.getTime()) ? null : d
}

// "Oct 15", or "Apr 9, 2025" when the date is not in the current year; "never" when absent.
export function formatShortDate(ts, now = new Date()) {
  const d = toDate(ts)
  if (!d) return ts ? String(ts) : 'never'
  const opts = { month: 'short', day: 'numeric' }
  if (d.getFullYear() !== now.getFullYear()) opts.year = 'numeric'
  return d.toLocaleDateString('en-US', opts)
}

// "Oct 15 07:23", with the year when it is not the current year.
export function formatShortDateTime(ts, now = new Date()) {
  const d = toDate(ts)
  if (!d) return ts ? String(ts) : 'never'
  const time = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })
  return `${formatShortDate(ts, now)} ${time}`
}

export function runStatusWord(status) {
  return STATUS_WORD[status] || status || 'unknown'
}

// lastLandedAt (optional): the newest last_synced_at of the connection's tables. The run
// history does not reach back to every load, so with no run recorded the tables still say
// when data last landed -- "no sync recorded" is not "never synced".
export function connectionHealth(conn, now = new Date(), { lastLandedAt = null } = {}) {
  const run = conn?.last_run || null
  const sched = conn?.schedule || null
  const historyError = conn?.history_error || null
  const scheduleError = conn?.schedule_error || null

  let tone
  if (run) tone = TONE[run.status] || 'unknown'
  else tone = historyError ? 'unknown' : 'never'

  const nextPart = sched
    ? (sched.enabled ? `next ${formatShortDateTime(sched.next_run_at, now)}` : 'schedule off')
    : (scheduleError ? "couldn't read the schedule" : null)

  let line
  if (historyError && !run) {
    line = "Couldn't read sync history"
    if (nextPart) line += ` · ${nextPart}`
  } else if (!run) {
    const landed = lastLandedAt ? ` · last landed ${formatShortDate(lastLandedAt, now)}` : ''
    line = `No sync recorded${landed} · ${nextPart || 'not scheduled'}`
  } else {
    const when = run.status === 'running'
      ? `since ${formatShortDateTime(run.started_at, now)}`
      : formatShortDateTime(run.completed_at || run.started_at, now)
    const runPart = `${runStatusWord(run.status)} · ${when} (${run.trigger_kind || 'manual'})`
    if (sched && sched.enabled) line = `Last sync: ${runPart} · ${nextPart}`
    else if (sched) line = `Schedule off · last run: ${runPart}`
    else if (scheduleError) line = `Last sync: ${runPart} · ${nextPart}`
    else line = `Not scheduled · last run: ${runPart}`
  }

  const saved = conn?.last_tested || conn?.created_at
  return {
    tone,
    statusWord: run ? runStatusWord(run.status) : (historyError ? 'unknown' : 'no sync recorded'),
    line,
    lastSyncAt: run ? (run.completed_at || run.started_at || null) : null,
    error: run && ['failed', 'interrupted', 'completed_with_errors'].includes(run.status) ? (run.error || null) : null,
    credentialsLine: saved ? `Credentials saved ${formatShortDate(saved, now)}` : null,
    readError: [historyError, scheduleError].filter(Boolean).join('; ') || null,
  }
}
